import { useEffect, useId, useRef, useState } from "react";
import { Pipette } from "lucide-react";
import { rasterAssetDataUrl, registerRasterAssetFromDataUrl } from "@/document/assets/rasterAssets";
import { hsbToOklch, hslToOklch, oklchColor, oklchToHsb, oklchToHsl, oklchToRgb, rgbToOklch, type OklchColor } from "@/document/model/color";
import {
  createImagePaint,
  createLinearGradientPaint,
  createSolidPaint,
  normalizeImagePaint,
  normalizeGradientPaint,
  type GradientPaint,
  type ImageAdjustments,
  type ImageFillMode,
  type ImagePaint,
  type Paint,
} from "@/document/model/styles";
import { ColorField } from "./ColorField";
import { HexEditor, HsbEditor, HslEditor, OklchEditor, RgbEditor } from "./ColorEditors";
import { ColorSlider, hueSliderBackground, opacitySliderBackground } from "./ColorSlider";
import { GradientEditor } from "./GradientEditor";
import { DraggablePanel, stackedPanelInitialOffset } from "./DraggablePanel";
import { panelFloatingUiAttribute } from "./PanelSelect";
import type { ColorSamplerRequest, ColorSamplerRequestWithCommit } from "../colorSampler";
import type { PickerMode } from "./types";

export function ColorPickerPopover({
  title,
  paint,
  allowImage = false,
  allowedModes,
  zIndexClass,
  activeColorSamplerSourceId,
  onChange,
  onColorSampleRequest,
  onColorSampleModeChange,
  onClose,
}: {
  title: string;
  paint: Paint;
  allowImage?: boolean;
  /** Restrict Solid / Gradient / Image tabs. Defaults from allowImage. */
  allowedModes?: PaintMode[];
  zIndexClass?: string;
  activeColorSamplerSourceId?: string;
  onChange: (paint: Paint) => void;
  onColorSampleRequest: (request: ColorSamplerRequestWithCommit) => void;
  onColorSampleModeChange: (sourceId: string, mode: PickerMode) => void;
  onClose: () => void;
}) {
  const [draftPaint, setDraftPaint] = useState<Paint>(paint);
  const [selectedStopId, setSelectedStopId] = useState<string | undefined>(
    isGradientPaint(paint) ? normalizeGradientPaint(paint).stops[0]?.id : undefined,
  );
  const [stopPickerStopId, setStopPickerStopId] = useState<string | undefined>(undefined);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const stopPickerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const frameRef = useRef(0);
  const rememberedGradientRef = useRef<GradientPaint | undefined>(isGradientPaint(paint) ? normalizeGradientPaint(paint) : undefined);
  const rememberedImageRef = useRef<ImagePaint | undefined>(paint.type === "image" ? normalizeImagePaint(paint) : undefined);

  useEffect(() => {
    setDraftPaint(paint);
    if (isGradientPaint(paint)) {
      const normalized = normalizeGradientPaint(paint);
      rememberedGradientRef.current = normalized;
      setSelectedStopId(current => normalized.stops.some(stop => stop.id === current) ? current : normalized.stops[0]?.id);
      setStopPickerStopId(current => normalized.stops.some(stop => stop.id === current) ? current : undefined);
    } else if (paint.type === "image") {
      rememberedImageRef.current = normalizeImagePaint(paint);
      setStopPickerStopId(undefined);
    } else {
      setStopPickerStopId(undefined);
    }
  }, [paint]);

  useEffect(() => {
    return () => cancelAnimationFrame(frameRef.current);
  }, []);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent): void {
      const target = event.target as Node;
      if (mainRef.current?.contains(target) || stopPickerRef.current?.contains(target)) {
        return;
      }

      if (target instanceof Element && target.closest(`[${panelFloatingUiAttribute}]`)) {
        return;
      }

      if (activeColorSamplerSourceId) {
        return;
      }

      onClose();
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [activeColorSamplerSourceId, onClose]);

  const normalizedDraftGradient = isGradientPaint(draftPaint) ? normalizeGradientPaint(draftPaint) : undefined;
  const activeStop = normalizedDraftGradient && stopPickerStopId
    ? normalizedDraftGradient.stops.find(stop => stop.id === selectedStopId)
      ?? normalizedDraftGradient.stops.find(stop => stop.id === stopPickerStopId)
      ?? normalizedDraftGradient.stops[0]
    : undefined;
  const paintModes: PaintMode[] = allowedModes
    ?? (allowImage ? ["solid", "gradient", "image"] : ["solid", "gradient"]);
  const showModeTabs = paintModes.length > 1;

  return (
    <>
      <DraggablePanel
        panelRef={mainRef}
        storageKey="paint-picker-main"
        title={title}
        widthClass="w-80"
        zIndexClass={zIndexClass}
        onClose={onClose}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={event => {
            const file = event.currentTarget.files?.[0];
            if (file) {
              void readImageFile(file)
                .then(imagePaint => commitPaint(imagePaint, true))
                .catch(() => undefined);
            }
            event.currentTarget.value = "";
          }}
        />
        {showModeTabs ? (
        <div className={`mb-3 grid ${paintModes.includes("image") ? "grid-cols-3" : "grid-cols-2"} gap-1 rounded bg-white/[0.04] p-1`}>
          {paintModes.map(candidate => (
            <button
              key={candidate}
              type="button"
              className={`rounded px-2 py-1 text-[11px] ${paintModeActive(draftPaint, candidate) ? "bg-sky-500 text-white" : "text-slate-400 hover:bg-white/10"}`}
              onClick={() => {
                if (paintModeActive(draftPaint, candidate)) {
                  return;
                }

                if (candidate === "solid") {
                  if (isGradientPaint(draftPaint)) {
                    rememberedGradientRef.current = normalizeGradientPaint(draftPaint);
                  } else if (draftPaint.type === "image") {
                    rememberedImageRef.current = normalizeImagePaint(draftPaint);
                  }
                  commitPaint(createSolidPaint(activeColor(draftPaint, selectedStopId)), true);
                  return;
                }

                if (candidate === "image") {
                  if (draftPaint.type === "image") {
                    return;
                  }
                  if (rememberedImageRef.current) {
                    commitPaint(rememberedImageRef.current, true);
                    return;
                  }
                  fileInputRef.current?.click();
                  return;
                }

                const nextPaint = rememberedGradientRef.current ?? createLinearGradientPaint(activeColor(draftPaint, selectedStopId), "#ffffff");
                setSelectedStopId(nextPaint.stops[0]?.id);
                setStopPickerStopId(undefined);
                commitPaint(nextPaint, true);
              }}
            >
              {candidate === "solid" ? "Solid" : candidate === "gradient" ? "Gradient" : "Image"}
            </button>
          ))}
        </div>
        ) : null}

        {draftPaint.type === "image" ? (
          <ImageControls
            paint={draftPaint}
            onChooseImage={() => fileInputRef.current?.click()}
            onDraftChange={nextPaint => setDraftPaint(nextPaint)}
            onCommit={nextPaint => commitPaint(nextPaint, true)}
          />
        ) : isGradientPaint(draftPaint) ? (
          <GradientEditor
            paint={draftPaint}
            selectedStopId={selectedStopId}
            onSelectedStopChange={setSelectedStopId}
            onStopColorClick={stopId => {
              setSelectedStopId(stopId);
              setStopPickerStopId(stopId);
            }}
            onChange={nextPaint => commitPaint(nextPaint)}
          />
        ) : (
          <ColorControls
            color={draftPaint.color}
            activeColorSamplerSourceId={activeColorSamplerSourceId}
            onColorSampleRequest={onColorSampleRequest}
            onColorSampleModeChange={onColorSampleModeChange}
            onChange={color => commitPaint(createSolidPaint(color))}
            onChangeEnd={color => commitPaint(createSolidPaint(color), true)}
          />
        )}
      </DraggablePanel>

      {isGradientPaint(draftPaint) && activeStop ? (
        <DraggablePanel
          panelRef={stopPickerRef}
          storageKey="paint-picker-stop-color"
          title="Stop color"
          widthClass="w-72"
          zIndexClass={zIndexClass}
          initialOffset={stackedPanelInitialOffset}
          onClose={() => setStopPickerStopId(undefined)}
        >
          <ColorControls
            color={activeStop.color}
            activeColorSamplerSourceId={activeColorSamplerSourceId}
            onColorSampleRequest={onColorSampleRequest}
            onColorSampleModeChange={onColorSampleModeChange}
            onChange={color => commitPaint(paintWithActiveColor(draftPaint, activeStop.id, color))}
            onChangeEnd={color => commitPaint(paintWithActiveColor(draftPaint, activeStop.id, color), true)}
          />
        </DraggablePanel>
      ) : null}
    </>
  );

  function commitPaint(nextPaint: Paint, immediate = false): void {
    setDraftPaint(nextPaint);
    if (isGradientPaint(nextPaint)) {
      rememberedGradientRef.current = normalizeGradientPaint(nextPaint);
    } else if (nextPaint.type === "image") {
      rememberedImageRef.current = normalizeImagePaint(nextPaint);
    }

    if (immediate) {
      cancelAnimationFrame(frameRef.current);
      onChange(nextPaint);
      return;
    }

    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => onChange(nextPaint));
  }
}

function ImageControls({
  paint,
  onChooseImage,
  onDraftChange,
  onCommit,
}: {
  paint: ImagePaint;
  onChooseImage: () => void;
  onDraftChange: (paint: ImagePaint) => void;
  onCommit: (paint: ImagePaint) => void;
}) {
  const normalized = normalizeImagePaint(paint);
  const previewSource = rasterAssetDataUrl(normalized.assetId, normalized.source);
  return (
    <div className="grid gap-3">
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          className="size-14 shrink-0 rounded-sm border border-white/15 bg-cover bg-center"
          style={{ backgroundImage: previewSource ? `url("${previewSource}")` : undefined }}
          onClick={onChooseImage}
          aria-label="Choose image fill"
        />
        <div className="min-w-0 flex-1">
          <button
            type="button"
            className="block w-full min-w-0 truncate text-left text-[12px] text-slate-100 hover:text-white"
            title={normalized.name ?? "Image fill"}
            onClick={onChooseImage}
          >
            <span className="block min-w-0 truncate">{normalized.name ?? "Image fill"}</span>
          </button>
          <div className="font-mono text-[11px] text-slate-500">{normalized.intrinsicWidth} x {normalized.intrinsicHeight}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1 rounded bg-white/[0.04] p-1">
        {(["fit", "fill", "crop"] as ImageFillMode[]).map(mode => (
          <button
            key={mode}
            type="button"
            className={`h-7 rounded px-2 text-[11px] capitalize ${normalized.fillMode === mode ? "bg-sky-500/20 text-sky-100" : "text-slate-400 hover:bg-white/10"}`}
            onClick={() => onCommit({ ...normalized, fillMode: mode })}
          >
            {mode}
          </button>
        ))}
      </div>

      <div className="grid gap-2">
        {imageAdjustmentRows.map(row => (
          <ImageAdjustmentSlider
            key={row.key}
            label={row.label}
            value={normalized.adjustments[row.key]}
            onDraftChange={value => onDraftChange(imagePaintWithAdjustment(normalized, row.key, value))}
            onCommit={value => onCommit(imagePaintWithAdjustment(normalized, row.key, value))}
          />
        ))}
      </div>
    </div>
  );
}

function ImageAdjustmentSlider({
  label,
  value,
  onDraftChange,
  onCommit,
}: {
  label: string;
  value: number;
  onDraftChange: (value: number) => void;
  onCommit: (value: number) => void;
}) {
  return (
    <label className="grid grid-cols-[72px_minmax(0,1fr)_40px] items-center gap-2 text-[11px] text-slate-400">
      <span className="truncate">{label}</span>
      <input
        type="range"
        min={-100}
        max={100}
        value={value}
        onChange={event => onDraftChange(Number(event.currentTarget.value))}
        onPointerUp={event => onCommit(Number(event.currentTarget.value))}
        onKeyUp={event => onCommit(Number(event.currentTarget.value))}
        className="h-5 accent-sky-400"
      />
      <span className="text-right font-mono text-slate-500">{Math.round(value)}</span>
    </label>
  );
}

function imagePaintWithAdjustment(paint: ImagePaint, key: keyof ImageAdjustments, value: number): ImagePaint {
  return {
    ...paint,
    adjustments: {
      ...paint.adjustments,
      [key]: value,
    },
  };
}

function ColorControls({
  color,
  activeColorSamplerSourceId,
  onColorSampleRequest,
  onColorSampleModeChange,
  onChange,
  onChangeEnd,
}: {
  color: OklchColor;
  activeColorSamplerSourceId?: string;
  onColorSampleRequest: (request: ColorSamplerRequestWithCommit) => void;
  onColorSampleModeChange: (sourceId: string, mode: PickerMode) => void;
  onChange: (color: OklchColor) => void;
  onChangeEnd: (color: OklchColor) => void;
}) {
  const [mode, setMode] = useState<PickerMode>("hex");
  const samplerSourceId = useId();
  const rgb = oklchToRgb(color);
  const hsl = oklchToHsl(color);
  const hsb = oklchToHsb(color);
  const hue = hueForMode(mode, color);
  const samplerActive = activeColorSamplerSourceId === samplerSourceId;

  useEffect(() => {
    if (samplerActive) {
      onColorSampleModeChange(samplerSourceId, mode);
    }
  }, [mode, onColorSampleModeChange, samplerActive, samplerSourceId]);

  return (
    <>
      <div className="mb-3 grid grid-cols-5 gap-1 rounded bg-white/[0.04] p-1">
        {(["hex", "rgb", "hsl", "hsb", "oklch"] as PickerMode[]).map(candidate => (
          <button
            key={candidate}
            type="button"
            className={`rounded px-2 py-1 text-[11px] uppercase ${mode === candidate ? "bg-sky-500 text-white" : "text-slate-400 hover:bg-white/10"}`}
            onClick={() => setMode(candidate)}
          >
            {candidate}
          </button>
        ))}
      </div>

      <ColorField mode={mode} color={color} onChange={onChange} onChangeEnd={onChangeEnd} />

      <div className="mb-3 grid grid-cols-[32px_minmax(0,1fr)] gap-2">
        <button
          type="button"
          className={`row-span-2 flex size-8 items-center justify-center rounded border text-slate-300 transition-colors ${
            samplerActive
              ? "border-sky-400 bg-sky-500/20 text-sky-100"
              : "border-white/10 bg-white/[0.04] hover:bg-white/10"
          }`}
          title="Sample color"
          aria-label="Sample color"
          aria-pressed={samplerActive}
          onClick={() => onColorSampleRequest({
            sourceId: samplerSourceId,
            mode,
            onSample: (sampledColor, _sampleStroke) => onChangeEnd(sampledColor),
          })}
        >
          <Pipette className="size-4" aria-hidden="true" />
        </button>
        <div className="grid gap-2">
          <ColorSlider
            label="Hue"
            min={0}
            max={360}
            value={hue}
            background={hueSliderBackground()}
            onChange={value => onChange(colorWithHue(mode, color, value))}
            onChangeEnd={value => onChangeEnd(colorWithHue(mode, color, value))}
          />
          <ColorSlider
            label="Opacity"
            min={0}
            max={100}
            value={color.alpha * 100}
            background={opacitySliderBackground(color)}
            onChange={value => onChange(oklchColor(color.l, color.c, color.h, value / 100))}
            onChangeEnd={value => onChangeEnd(oklchColor(color.l, color.c, color.h, value / 100))}
          />
        </div>
      </div>

      {mode === "hex" ? <HexEditor color={color} onChange={onChangeEnd} /> : null}
      {mode === "rgb" ? <RgbEditor color={rgb} onChange={next => onChangeEnd(rgbToOklch(next))} /> : null}
      {mode === "hsl" ? <HslEditor color={hsl} onChange={next => onChangeEnd(hslToOklch(next))} /> : null}
      {mode === "hsb" ? <HsbEditor color={hsb} onChange={next => onChangeEnd(hsbToOklch(next))} /> : null}
      {mode === "oklch" ? <OklchEditor color={color} onChange={onChangeEnd} /> : null}
    </>
  );
}

function activeColor(paint: Paint, selectedStopId?: string): OklchColor {
  if (paint.type === "solid") {
    return paint.color;
  }

  if (paint.type === "image") {
    return createSolidPaint("#ffffff").color;
  }

  const normalized = normalizeGradientPaint(paint);
  return normalized.stops.find(stop => stop.id === selectedStopId)?.color ?? normalized.stops[0]!.color;
}

function paintWithActiveColor(paint: Paint, selectedStopId: string | undefined, color: OklchColor): Paint {
  if (paint.type === "solid") {
    return createSolidPaint(color);
  }

  if (paint.type === "image") {
    return paint;
  }

  const normalized = normalizeGradientPaint(paint);
  const activeStopId = selectedStopId ?? normalized.stops[0]?.id;
  return {
    ...normalized,
    stops: normalized.stops.map(stop => stop.id === activeStopId ? { ...stop, color } : stop),
  } satisfies GradientPaint;
}

function isGradientPaint(paint: Paint): paint is GradientPaint {
  return paint.type === "linearGradient" || paint.type === "radialGradient";
}

type PaintMode = "solid" | "gradient" | "image";

function paintModeActive(paint: Paint, mode: PaintMode): boolean {
  if (mode === "solid") {
    return paint.type === "solid";
  }

  if (mode === "image") {
    return paint.type === "image";
  }

  return isGradientPaint(paint);
}

const imageAdjustmentRows: Array<{ key: keyof ImageAdjustments; label: string }> = [
  { key: "exposure", label: "Exposure" },
  { key: "contrast", label: "Contrast" },
  { key: "saturation", label: "Saturation" },
  { key: "temperature", label: "Temperature" },
  { key: "tint", label: "Tint" },
  { key: "highlights", label: "Highlights" },
  { key: "shadows", label: "Shadows" },
];

async function readImageFile(file: File): Promise<ImagePaint> {
  const source = await readFileAsDataUrl(file);
  const dimensions = await imageDimensions(source);
  const assetId = registerRasterAssetFromDataUrl({
    dataUrl: source,
    name: file.name,
    mimeType: file.type,
  });
  return createImagePaint({
    assetId,
    name: file.name,
    mimeType: file.type,
    intrinsicWidth: dimensions.width,
    intrinsicHeight: dimensions.height,
  });
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Could not read image file"));
    reader.onerror = () => reject(reader.error ?? new Error("Could not read image file"));
    reader.readAsDataURL(file);
  });
}

function imageDimensions(source: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: Math.max(1, image.naturalWidth), height: Math.max(1, image.naturalHeight) });
    image.onerror = () => reject(new Error("Could not load image dimensions"));
    image.src = source;
  });
}

function hueForMode(mode: PickerMode, color: OklchColor): number {
  if (mode === "hsl") {
    return oklchToHsl(color).h;
  }

  if (mode === "oklch") {
    return color.h;
  }

  return oklchToHsb(color).h;
}

function colorWithHue(mode: PickerMode, color: OklchColor, hue: number): OklchColor {
  if (mode === "hsl") {
    return hslToOklch({ ...oklchToHsl(color), h: hue });
  }

  if (mode === "oklch") {
    return oklchColor(color.l, color.c, hue, color.alpha);
  }

  return hsbToOklch({ ...oklchToHsb(color), h: hue });
}
